import { useSuspenseQuery } from "@apollo/client/react";
import { parseISO } from "date-fns";
import { Pencil } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DetailLoading } from "~/components/loading";

import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import {
  GetEvidenceImporterDetailDocument,
  type GetEvidenceImporterDetailQuery,
  type GetEvidenceImporterDetailQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import { useEvidenceImporterActivation } from "~/features/dataImportProjects/hooks/useEvidenceImporterActivation";
import { useTenant } from "~/features/tenant/context";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";
import { EvidenceImporterEditForm } from "./EvidenceImporterEditForm";

type EvidenceImporter = GetEvidenceImporterDetailQuery["evidenceImporters"][number];

type EvidenceImporterDetailPanelProps = {
  evidenceImporterId: string;
};

function formatDate(t: ReturnType<typeof useTranslation>["t"], iso: string | null | undefined) {
  return iso ? t("date.short", { date: parseISO(iso) }) : "-";
}

function EvidenceImporterEditDialog({
  open,
  onOpenChange,
  importer,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  importer: EvidenceImporter;
  onSaved: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("ui.edit")} {t("model.evidenceImporter.modelName")}
          </DialogTitle>
        </DialogHeader>
        {open ? (
          <div className="min-w-[480px]">
            <Suspense fallback={<DetailLoading />}>
              <EvidenceImporterEditForm
                key={importer.id}
                importProjectId={importer.dataImportProjectId}
                dataSourceId={importer.dataSourceId}
                evidenceImporterId={importer.id}
                onSubmit={onSaved}
              />
            </Suspense>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function EvidenceImporterDetailPanel({ evidenceImporterId }: EvidenceImporterDetailPanelProps) {
  const { t } = useTranslation();
  const { isReadOnly, projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(`evidence-importer:${evidenceImporterId}`);
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const [openEditForm, setOpenEditForm] = useState(false);

  useEffect(() => {
    if (isReadOnly) setOpenEditForm(false);
  }, [isReadOnly]);

  const { onActivate, onDeactivate } = useEvidenceImporterActivation({ canExecute: canExecuteMutation });
  const { data, refetch } = useSuspenseQuery<GetEvidenceImporterDetailQuery, GetEvidenceImporterDetailQueryVariables>(
    GetEvidenceImporterDetailDocument,
    {
      variables: { tenantId, id: evidenceImporterId },
    },
  );

  const importer = data.evidenceImporters[0];
  const isAuthorized = importer?.dataImportProjectId === projectId;
  setAuthorized(isAuthorized);
  if (!importer || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  return (
    <div className="space-y-4">
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[18px] font-semibold text-fg-1">{importer.name}</h2>
              {importer.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-fg-3">
              <div className="flex gap-1.5">
                <dt>{t("model.common.id")}:</dt>
                <dd className="font-mono">{importer.id}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.createdAt")}:</dt>
                <dd>{formatDate(t, importer.createdAt)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.updatedAt")}:</dt>
                <dd>{formatDate(t, importer.updatedAt)}</dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.edit")}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenEditForm(true);
                }}
              >
                <Pencil className="size-3.5" />
                {t("ui.edit")}
              </Button>
            </DipReadOnlyTooltip>
            {importer.deactivatedAt ? (
              <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.activate")}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isReadOnly}
                  onClick={() => {
                    if (!canExecuteMutation()) return;
                    onActivate({ id: importer.id, name: importer.name });
                  }}
                >
                  {t("ui.activate")}
                </Button>
              </DipReadOnlyTooltip>
            ) : (
              <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.deactivate")}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isReadOnly}
                  onClick={() => {
                    if (!canExecuteMutation()) return;
                    onDeactivate({ id: importer.id, name: importer.name });
                  }}
                >
                  {t("ui.deactivate")}
                </Button>
              </DipReadOnlyTooltip>
            )}
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-14-bold text-fg-1">{t("model.evidenceImporter.definition")}</h3>
        <pre className="max-h-[400px] overflow-auto rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-3 text-12-regular text-fg-1">
          {JSON.stringify(importer.definition, null, 2)}
        </pre>
      </section>

      {!isReadOnly ? (
        <EvidenceImporterEditDialog
          open={openEditForm && !isReadOnly}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditForm(open);
          }}
          importer={importer}
          onSaved={() => {
            setOpenEditForm(false);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}
